import React, { useState } from 'react';
import { Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';

const TrackOrderPage = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleTrackOrder = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOrder(null);

    try {
      const response = await api.post('/orders/track', {
        order_number: orderNumber,
        email: email
      });
      setOrder(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Order not found. Please check your details.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-600',
      processing: 'text-blue-600',
      shipped: 'text-purple-600',
      delivered: 'text-green-600',
      cancelled: 'text-red-600'
    };
    return colors[status] || 'text-gray-600';
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Package className="w-16 h-16 mx-auto mb-4" />
          <h1 className="font-serif text-4xl mb-2">Track Your Order</h1>
          <p className="text-gray-600">Enter your order details to track your shipment</p>
        </div>

        <form onSubmit={handleTrackOrder} className="bg-white border border-gray-200 p-6 rounded-lg mb-8">
          <div className="space-y-4">
            <div>
              <Label htmlFor="orderNumber">Order Number</Label>
              <Input
                id="orderNumber"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g., ORD-123456"
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white hover:bg-gray-900"
            >
              {loading ? 'Tracking...' : 'Track Order'}
            </Button>
          </div>
        </form>

        {order && (
          <div className="bg-white border border-gray-200 p-6 rounded-lg">
            <h2 className="font-serif text-2xl mb-6">Order Information</h2>

            <div className="space-y-4">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Order Number:</span>
                <span className="font-medium">{order.order_number}</span>
              </div>

              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Status:</span>
                <span className={`font-medium capitalize ${getStatusColor(order.status)}`}>
                  {order.status}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Order Date:</span>
                <span className="font-medium">
                  {new Date(order.created_at).toLocaleDateString()}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Total:</span>
                <span className="font-medium">{formatPrice(order.total, order.currency)}</span>
              </div>

              {order.tracking_number && (
                <div className="py-4 bg-gray-50 px-4 rounded">
                  <p className="text-sm text-gray-600 mb-1">Tracking Number</p>
                  <p className="font-mono font-medium">{order.tracking_number}</p>
                  {order.tracking_url && (
                    <a
                      href={order.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-black underline mt-2 inline-block"
                    >
                      Track Package →
                    </a>
                  )}
                </div>
              )}

              {order.items && order.items.length > 0 && (
                <div className="pt-4">
                  <h3 className="font-serif text-xl mb-4">Order Items</h3>
                  <div className="space-y-3">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex gap-4">
                        <img
                          src={item.main_image_url || 'https://images.unsplash.com/photo-1689777239202-a7a34baae6d7?w=100&q=80'}
                          alt={item.product_name}
                          className="w-16 h-16 object-cover"
                        />
                        <div className="flex-1">
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                        </div>
                        <p className="font-medium">{formatPrice(item.price_at_time * item.quantity)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrderPage;
